/**
 * SectionDesigns — 섹션 라이브러리 관리 (FIKET-156 S4-a)
 *
 * 좌: 자산 목록(cover/본문 섹션/전역 스타일시트), 우: 편집 패널(즉시 표시).
 * 백엔드 CRUD(section-designs, stylesheets)는 이미 완비 — 신규 API 없음.
 *
 * 주의: 서버의 isActive 쿼리 파라미터는 z.coerce.boolean() 로 파싱하므로
 * 문자열 "false"가 Boolean("false")=true 로 강제변환되어 비활성 조회가 불가함.
 * 따라서 기본 목록(활성 항목만)만 한 번 조회한다.
 */
import { useState, useMemo, useCallback } from 'react'
import { useApiFetch } from '../hooks/useApiFetch'
import { Spinner, EmptyState } from '@/ui'
import { SectionLibrarySidebar, type LibrarySelection } from './section-designs/SectionLibrarySidebar'
import { SectionDesignPanel } from './section-designs/SectionDesignPanel'
import { StylesheetPanel } from './section-designs/StylesheetPanel'
import type { SectionDesign, Stylesheet } from './section-designs/types'
import styles from './SectionDesigns.module.css'

export function SectionDesigns() {
  const { data: sectionsData, isLoading: l1, refetch: r1 } = useApiFetch<{ sectionDesigns: SectionDesign[] }>(
    '/api/admin/section-designs',
  )
  const { data: ssData, isLoading: l2, refetch: r2 } = useApiFetch<{ stylesheets: Stylesheet[] }>(
    '/api/admin/stylesheets',
  )

  const sections = useMemo(() => {
    const list = sectionsData?.sectionDesigns ?? []
    return [...list].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
  }, [sectionsData])
  const stylesheets = ssData?.stylesheets ?? []

  const [selection, setSelection] = useState<LibrarySelection>(null)

  // 미리보기에 적용할 전역 스타일시트. section_designs 에 시트 참조 컬럼이 없어
  // 화면 상태로만 관리한다. 페이지 레벨에 둬서 섹션을 바꿔도 선택이 유지되게 한다.
  // '' = 전역 시트 없이 렌더.
  const [stylesheetId, setStylesheetId] = useState<string | null>(null)
  const effectiveStylesheetId = stylesheetId ?? stylesheets[0]?.id ?? ''

  const refetchAll = useCallback(() => { r1(); r2() }, [r1, r2])

  const selectedSection = useMemo(() => {
    if (selection?.kind === 'section') return sections.find((s) => s.id === selection.id) ?? null
    return null
  }, [selection, sections])
  const selectedStylesheet = useMemo(() => {
    if (selection?.kind === 'stylesheet') return stylesheets.find((s) => s.id === selection.id) ?? null
    return null
  }, [selection, stylesheets])

  const handleSectionSaved = useCallback(() => { refetchAll() }, [refetchAll])
  const handleSectionDeleted = useCallback(() => { setSelection(null); refetchAll() }, [refetchAll])

  if (l1 || l2) {
    return <div className={styles.loadingFull}><Spinner /></div>
  }

  return (
    <div className={styles.page}>
      <SectionLibrarySidebar
        sections={sections}
        stylesheets={stylesheets}
        selection={selection}
        onSelect={setSelection}
      />
      <div className={styles.detail}>
        {selection === null && (
          <EmptyState title="자산을 선택하세요" description="왼쪽에서 섹션 디자인 또는 전역 스타일시트를 선택하거나 새로 만드세요" />
        )}
        {(selection?.kind === 'section' || selection?.kind === 'new-section') && (
          <SectionDesignPanel
            key={selection.kind === 'section' ? selection.id : 'new-section'}
            section={selectedSection}
            stylesheets={stylesheets}
            stylesheetId={effectiveStylesheetId}
            onStylesheetChange={setStylesheetId}
            onSaved={handleSectionSaved}
            onDeleted={handleSectionDeleted}
          />
        )}
        {(selection?.kind === 'stylesheet' || selection?.kind === 'new-stylesheet') && (
          <StylesheetPanel
            key={selection.kind === 'stylesheet' ? selection.id : 'new-stylesheet'}
            stylesheet={selectedStylesheet}
            onSaved={refetchAll}
            onDeleted={() => { setSelection(null); refetchAll() }}
          />
        )}
      </div>
    </div>
  )
}
